import OpenInNewIcon from '@mui/icons-material/OpenInNewOutlined';
import PropTypes from 'prop-types';
import React from 'react';
import routePath, {Routes} from '@account/utils/route';
import {
  Box,
  Button,
  Chip,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import {DateTime} from 'luxon';
import {Link as RouterLink} from 'react-router-dom';
import {dateTimeStamp} from '@account/utils/dates';

const TYPE_DESCRIPTIONS = {
  product_addition:
    'A new product entitlement was manually added to this Customer Account.',
  quota_adjustment:
    "An existing entitlement's quota was manually adjusted on this Customer Account.",
};

const JIRA_BASE = 'https://hello.planet.com/jira/browse';

const Field = ({label, children}) =>
  children ? (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      {typeof children === 'string' ? (
        <Typography variant="body2">{children}</Typography>
      ) : (
        children
      )}
    </Box>
  ) : null;

Field.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const EmergencyOverrideDetailPanel = ({row}) => {
  const accountId = row.ca_id ?? row.org_id;
  const accountName = row.ca_name ?? row.org_name;
  return (
    <Box sx={{p: 3, pl: 6, bgcolor: 'grey.50'}}>
      <Stack spacing={2}>
        <Box>
          <Chip
            label={
              row.type === 'product_addition'
                ? 'Product Addition'
                : row.type === 'quota_adjustment'
                  ? 'Quota Adjustment'
                  : row.type
            }
            color={row.type === 'product_addition' ? 'primary' : 'warning'}
            size="small"
            variant="outlined"
          />
          <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
            {TYPE_DESCRIPTIONS[row.type] || 'Manual provisioning action.'}
          </Typography>
        </Box>

        <Divider />

        <Stack direction="row" spacing={4} flexWrap="wrap">
          <Field label="Customer Account">
            {accountId && accountName ? (
              <Link
                component={RouterLink}
                to={routePath(Routes.ACCOUNT_DETAIL, {accountId})}
                variant="body2"
                underline="hover"
              >
                {accountName}
              </Link>
            ) : (
              accountName || '—'
            )}
          </Field>

          <Field label="Product">{row.product_name}</Field>

          {row.edp_id && <Field label="EDP">{row.edp_id}</Field>}

          <Field label="JIRA Ticket">
            {row.jira_ticket ? (
              <Link
                href={`${JIRA_BASE}/${row.jira_ticket}`}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                underline="hover"
              >
                {row.jira_ticket}
                <OpenInNewIcon
                  sx={{fontSize: 12, ml: 0.5, verticalAlign: 'middle'}}
                />
              </Link>
            ) : null}
          </Field>

          <Field label="Created By">{row.created_by}</Field>

          <Field label="Date">{dateTimeStamp(row.created_at)}</Field>
        </Stack>

        {row.reason && (
          <>
            <Divider />
            <Field label="Reason">
              <Typography variant="body2" sx={{whiteSpace: 'pre-wrap'}}>
                {row.reason}
              </Typography>
            </Field>
          </>
        )}

        {(row.quota_limit !== undefined ||
          row.previous_quota_limit !== undefined) && (
          <>
            <Divider />
            <Stack direction="row" spacing={4}>
              {row.previous_quota_limit !== undefined && (
                <Field label="Previous Quota">
                  {row.previous_quota_limit === null
                    ? 'Unlimited'
                    : Number(row.previous_quota_limit).toLocaleString()}
                </Field>
              )}
              {row.quota_limit !== undefined && (
                <Field label="New Quota">
                  {row.quota_limit === null
                    ? 'Unlimited'
                    : Number(row.quota_limit).toLocaleString()}
                </Field>
              )}
            </Stack>
          </>
        )}

        {(row.start_date || row.end_date) && (
          <Stack direction="row" spacing={4}>
            {row.start_date && (
              <Field label="Start Date">
                {DateTime.fromISO(row.start_date).toLocaleString(
                  DateTime.DATE_MED
                )}
              </Field>
            )}
            {row.end_date && (
              <Field label="End Date">
                {DateTime.fromISO(row.end_date).toLocaleString(
                  DateTime.DATE_MED
                )}
              </Field>
            )}
          </Stack>
        )}

        {(accountId || row.contract_id) && (
          <>
            <Divider />
            <Stack direction="row" spacing={1}>
              {accountId && (
                <Button
                  component={RouterLink}
                  to={routePath(Routes.ACCOUNT_DETAIL, {accountId})}
                  size="small"
                  variant="outlined"
                >
                  View Account
                </Button>
              )}
              {row.contract_id && (
                <Button
                  component={RouterLink}
                  to={routePath(Routes.CONTRACT_DETAIL, {
                    contractId: row.contract_id,
                  })}
                  size="small"
                  variant="outlined"
                >
                  View Contract
                </Button>
              )}
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
};

EmergencyOverrideDetailPanel.propTypes = {
  row: PropTypes.object.isRequired,
};

export default EmergencyOverrideDetailPanel;
